import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  IconButton,
  Spinner,
  Stack,
  Text,
  useColorModeValue,
  VStack,
} from '@ecoinvent/ui';
import { BiArrowToRight } from 'react-icons/bi';

import { FlowDetails } from 'hooks/data/datasets/V4/flows/types';

import SankeyDiagram from '../SankeyDiagram';

import ClassificationTable from './ClassificationTable';
import PropertiesTable from './PropertiesTable';
import SourcesTable from './SuppliersTable';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  isFullScreen: boolean;
  setIsFullScreen: (isFullScreen: boolean) => void;
  flowDetails?: FlowDetails;
  isLoading?: boolean;
};

const FlowDetailDrawer = ({ isOpen, onClose, isFullScreen, setIsFullScreen, flowDetails, isLoading = false }: Props) => {
  const stateTextColor = useColorModeValue('gray.600', 'gray.400');

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size={isFullScreen ? 'full' : 'xl'}>
      <DrawerOverlay />
      <DrawerContent p={12}>
        <DrawerCloseButton display="none" />
        <DrawerHeader>
          <Stack direction="row" alignItems="center">
            <IconButton
              variant="outline"
              colorScheme="gray"
              onClick={onClose}
              aria-label="Close drawer"
              icon={<BiArrowToRight />}
            />
            <Text>{flowDetails?.name || 'Loading...'}</Text>
          </Stack>
        </DrawerHeader>
        <DrawerBody>
          {isLoading ? (
            <VStack spacing={4} align="center" justify="center" h="full" minH="400px">
              <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="green.500" size="xl" />
              <Text color={stateTextColor} fontSize="md" fontWeight="medium">
                Loading flow details...
              </Text>
            </VStack>
          ) : !flowDetails ? (
            <VStack spacing={4} align="center" justify="center" h="full" minH="400px">
              <Text color={stateTextColor} fontSize="md">
                No flow details available.
              </Text>
            </VStack>
          ) : (
            <Stack spacing={8}>
              {(flowDetails.classification.CPC || flowDetails.classification.HS2017) && (
                <Stack>
                  <Text fontWeight={700}>Classification</Text>
                  <ClassificationTable classification={flowDetails.classification} />
                </Stack>
              )}
              {flowDetails.comment && flowDetails.comment.length > 0 && (
                <Stack>
                  <Text fontWeight={700}>Comments</Text>
                  <Text>{flowDetails.comment}</Text>
                </Stack>
              )}
              {flowDetails.sources && flowDetails.sources.length > 1 && (
                <Stack spacing={4}>
                  <Text fontWeight={700}>Supply Distribution</Text>
                  <SankeyDiagram data={flowDetails} isFullScreen={isFullScreen} setIsFullScreen={setIsFullScreen} />
                  <Text fontWeight={700} mt={4}>
                    Suppliers
                  </Text>
                  <SourcesTable suppliers={flowDetails.sources} />
                </Stack>
              )}
              {flowDetails.properties && flowDetails.properties.length > 0 && (
                <Stack>
                  <Text fontWeight={700}>Properties</Text>
                  <PropertiesTable properties={flowDetails.properties} />
                </Stack>
              )}
            </Stack>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default FlowDetailDrawer;
